/**
 * useCalendarSettings Hook
 * React Query hook for calendar settings
 * Handles both fetching and updating settings
 *
 * API Response Format:
 * {
 *   calendar_settings: {
 *     show_weekends: boolean,
 *     show_birthdays: boolean,
 *     show_sol: boolean,
 *     show_business_times_only: boolean,
 *     sync_with_google_calendar: boolean,
 *     sync_with_outlook: boolean,
 *     calendar_id: string | null,
 *     default_calendar_view: string,
 *     primary: boolean
 *   }
 * }
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCalendarSettings, updateCalendarSettings } from '../api/calendarApi';

/**
 * Calendar settings query hook
 *
 * @param {object} options - Query options
 * @param {boolean} options.enabled - Enable/disable query
 * @returns {object} React Query result with mutation
 */
export const useCalendarSettings = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();

  // Query for fetching settings
  const query = useQuery({
    queryKey: ['calendar', 'settings'],
    queryFn: async () => {
      const result = await fetchCalendarSettings();
      // Return the calendar_settings object directly
      return result.data?.calendar_settings || null;
    },
    enabled,
    staleTime: 60 * 60 * 1000, // 60 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });

  // Mutation for updating settings
  const mutation = useMutation({
    mutationFn: async (newSettings) => {
      const result = await updateCalendarSettings(newSettings);
      // Return the calendar_settings object directly
      return result.data?.calendar_settings || newSettings;
    },
    onMutate: async (newSettings) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['calendar', 'settings'] });

      // Snapshot previous value
      const previousSettings = queryClient.getQueryData(['calendar', 'settings']);

      // Optimistically update with new settings
      queryClient.setQueryData(['calendar', 'settings'], (old) => ({
        ...old,
        ...newSettings,
      }));

      return { previousSettings };
    },
    onError: (err, newSettings, context) => {
      // Rollback on error
      queryClient.setQueryData(['calendar', 'settings'], context.previousSettings);
    },
    onSuccess: (data) => {
      // Update with server response
      queryClient.setQueryData(['calendar', 'settings'], data);
    },
  });

  return {
    settings: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    updateSettings: mutation.mutate,
    updateSettingsAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
};

export default useCalendarSettings;
